// What the kiosk says. The words are written in az, ru and en; any other customer
// language reads English until the platform's language files carry it.
//
// The customer chooses a language on the first screen and the whole flow follows
// it, including the service names, which come from the catalogue rather than from
// here. A missing translation falls back rather than showing a key: a button with
// no words is broken, a button in the wrong language is merely inconvenient.
import { LEGACY_CUSTOMER, nativeName, resolveText } from './i18n/customer'
import type { LanguageCode } from './i18n/languages'

import { wordText } from './i18n/catalog'
import { mayNotBeServed, waitText, type Estimate } from './estimate'

/** A customer language (lib/i18n/languages.ts). */
export type Language = LanguageCode

/** The languages these words are written in. */
type Written = 'az' | 'ru' | 'en'

/** A language button per offered language, in order, labelled in itself. */
export function languageButtons(codes: readonly LanguageCode[]): { code: Language; label: string }[] {
  return codes.map((code) => ({ code, label: nativeName(code) }))
}

/** The three every organisation offered before languages were a setting. */
export const languages = languageButtons(LEGACY_CUSTOMER)

type Phrase = Record<Written, string>

export const phrases = {
  touchToStart: {
    az: 'Talon götürmək üçün toxunun',
    ru: 'Коснитесь, чтобы взять талон',
    en: 'Touch to take a ticket',
  },
  chooseService: {
    az: 'Xidməti seçin',
    ru: 'Выберите услугу',
    en: 'Choose a service',
  },
  issuing: {
    az: 'Talonunuz hazırlanır…',
    ru: 'Готовим ваш талон…',
    en: 'Preparing your ticket…',
  },
  yourNumber: {
    az: 'Sizin nömrəniz',
    ru: 'Ваш номер',
    en: 'Your number',
  },
  peopleAhead: {
    az: 'Qabağınızda',
    ru: 'Перед вами',
    en: 'Ahead of you',
  },
  approxWait: {
    az: 'Təxmini gözləmə',
    ru: 'Примерное ожидание',
    en: 'Approximate wait',
  },
  nextInLine: {
    az: 'Növbəti sizsiniz',
    ru: 'Вы следующий',
    en: 'You are next',
  },
  watchTheScreen: {
    az: 'Zəhmət olmasa ekrana baxın',
    ru: 'Пожалуйста, следите за экраном',
    en: 'Please watch the screen',
  },
  minutes: {
    az: 'dəq',
    ru: 'мин',
    en: 'min',
  },
  overTwoHours: {
    az: '2 saatdan çox',
    ru: 'более 2 часов',
    en: 'over 2 hours',
  },
  // The wait runs past the branch's closing time (engine.Estimate pastClosing).
  mayNotBeServed: { az: '', ru: '', en: 'May not be served today' },
  closed: {
    az: 'Bağlıdır',
    ru: 'Закрыто',
    en: 'Closed',
  },
  // The kiosk's closed screen: the heading, then opensWords below it.
  weAreClosed: {
    az: 'Bağlıyıq',
    ru: 'Мы закрыты',
    en: "We're closed",
  },
  back: {
    az: 'Geri',
    ru: 'Назад',
    en: 'Back',
  },
  unavailable: {
    az: 'Müvəqqəti əlçatmazdır — zəhmət olmasa qəbula müraciət edin',
    ru: 'Временно недоступно — пожалуйста, обратитесь к администратору',
    en: 'Temporarily unavailable — please see reception',
  },
  tryAgain: {
    az: 'Yenidən cəhd edin',
    ru: 'Попробуйте снова',
    en: 'Try again',
  },
  // The machine's burst limit, which is about the machine and not about the person
  // reading it. "Try again" is the wrong instruction here — trying again
  // immediately is precisely what will fail — so the words ask for a pause and name
  // the way out.
  busy: {
    az: 'Kiosk məşğuldur — bir az gözləyin',
    ru: 'Киоск занят — подождите немного',
    en: 'This machine is busy — please wait a moment',
  },
  orSeeReception: {
    az: 'və ya qəbula müraciət edin',
    ru: 'или обратитесь к администратору',
    en: 'or see reception',
  },
  // The branch's own rate limit, not the machine's: trying again shortly is the
  // right answer here, unlike TOO_MANY_TICKETS above.
  branchBusy: {
    az: 'Məşğuldur',
    ru: 'Занято',
    en: 'Busy',
  },
  noServices: {
    az: 'Bu filialda hazırda xidmət təklif olunmur',
    ru: 'В этом отделении сейчас нет доступных услуг',
    en: 'No services are being offered here at the moment',
  },
  // The page a slip's code opens on the customer's phone.
  followWaiting: { az: 'Növbədəsiniz', ru: 'Вы в очереди', en: 'You are in the queue' },
  followCalled: { az: 'Növbəniz çatdı', ru: 'Ваша очередь', en: 'It is your turn' },
  followGoTo: { az: 'Yaxınlaşın:', ru: 'Подойдите к:', en: 'Please go to' },
  followServing: { az: 'Sizə xidmət göstərilir', ru: 'Вас обслуживают', en: 'You are being served' },
  followParked: {
    az: 'Zəhmət olmasa gözləyin — sizi yenidən çağıracağıq',
    ru: 'Пожалуйста, подождите — вас вызовут снова',
    en: 'Please wait — you will be called again',
  },
  followMissed: {
    az: 'Çağırışı buraxdınız — qəbula yaxınlaşın',
    ru: 'Вы пропустили вызов — обратитесь к администратору',
    en: 'You missed your call — please see reception',
  },
  followDone: {
    az: 'Xidmət başa çatdı. Təşəkkür edirik!',
    ru: 'Обслуживание завершено. Спасибо!',
    en: 'Your visit is complete. Thank you!',
  },
  followGone: {
    az: 'Bu talon artıq növbədə deyil',
    ru: 'Этого талона больше нет в очереди',
    en: 'This ticket is no longer in the queue',
  },
  followLeft: { az: 'Növbədən çıxdınız', ru: 'Вы вышли из очереди', en: 'You have left the queue' },
  followEnded: {
    az: 'Bu talon artıq etibarlı deyil',
    ru: 'Этот талон больше не действует',
    en: 'This ticket is no longer current',
  },
  followNotFound: { az: 'Bu keçid tanınmır', ru: 'Эта ссылка не распознана', en: 'This link is not recognised' },
  followOffline: {
    az: 'Əlaqə yoxdur — yenidən cəhd edilir',
    ru: 'Нет связи — повторяем попытку',
    en: 'No connection — trying again',
  },
  followBusy: {
    az: 'Çox sorğu var — bir dəqiqə gözləyin',
    ru: 'Слишком много запросов — подождите минуту',
    en: 'Too many requests — please wait a minute',
  },
  followLeave: { az: 'Növbədən çıxmaq', ru: 'Выйти из очереди', en: 'Leave the queue' },
  followLeaveConfirm: {
    az: 'Əminsiniz? Nömrəniz ləğv ediləcək və bərpa edilə bilməz.',
    ru: 'Вы уверены? Ваш номер будет аннулирован без возможности восстановления.',
    en: 'Are you sure? Your number will be cancelled, and this cannot be undone.',
  },
  followYesLeave: { az: 'Bəli, çıxıram', ru: 'Да, выйти', en: 'Yes, leave' },
  followStay: { az: 'Qalıram', ru: 'Остаться', en: 'Stay' },

  // The follow page ringing its holder, and the box that takes a number to text.
  // Native review pending (az, ru).
  followAlertMe: { az: 'Növbəm çatanda bu telefona xəbər ver', ru: 'Сообщить на этот телефон, когда подойдёт очередь', en: 'Alert me on this phone when my turn comes' },
  followAlertOn: { az: 'Növbəniz çatanda telefon səs siqnalı verib titrəyəcək', ru: 'Когда подойдёт очередь, телефон подаст сигнал и завибрирует', en: 'Your phone will ring and vibrate when your turn comes' },
  followAlertPageOnly: { az: 'Bu səhifəni açıq saxlayın — növbəniz çatanda səs verəcək', ru: 'Не закрывайте эту страницу — она подаст сигнал, когда подойдёт очередь', en: 'Keep this page open — it will ring when your turn comes' },
  followComeBack: { az: 'Zəhmət olmasa, qayıdın', ru: 'Пожалуйста, возвращайтесь', en: 'Please make your way back' },
  followTextMe: { az: 'Növbəm yaxınlaşanda SMS göndərin', ru: 'Прислать SMS, когда очередь подойдёт', en: 'Text me as my turn comes' },
  followTextSave: { az: 'Göndərin', ru: 'Присылать', en: 'Text me' },
  followTextOn: { az: 'SMS göndəriləcək:', ru: 'SMS будут приходить на:', en: 'We will text' },
  followTextStop: { az: 'SMS göndərməyin', ru: 'Не присылать SMS', en: 'Stop texts' },
  followTextKept: { az: 'Telefon nömrəniz yalnız bu talon üçün istifadə olunur və {n} gün sonra silinir.', ru: 'Ваш номер телефона используется только для этого талона и удаляется через {n} дн.', en: 'Your number is used for this visit only and deleted {n} days after it.' },
  followTextBad: { az: 'Nömrəni ölkə kodu ilə yazın, məsələn +994 50 123 45 67', ru: 'Укажите номер с кодом страны, например +994 50 123 45 67', en: 'Write the number with its country code, as +994 50 123 45 67' },
  followTextRefused: { az: 'Bu nömrəyə SMS göndərilmir', ru: 'На этот номер SMS не отправляются', en: 'Texts are not sent to this number' },
  followTextHeld: { az: 'Bu talon üçün nömrəniz:', ru: 'Ваш номер для этого талона:', en: 'Your number on this visit:' },
  followTextOff: { az: 'SMS artıq təklif olunmur', ru: 'SMS больше не предлагаются', en: 'Texts are no longer offered' },
  followDismiss: { az: 'Bağla', ru: 'Закрыть', en: 'Close' },

  // The hall board's own words, in the machine's language (lib/units.boardLanguage).
  // "Opens" rather than "Opens again", because a board says it under a branch name
  // and a time, where the kiosk says it to somebody who tried to take a ticket.
  opens: {
    az: 'Açılış:',
    ru: 'Откроется',
    en: 'Opens',
  },
  // Empty: a board with no calls shows nothing until a design or the organisation writes a line.
  boardNoCalls: { az: '', ru: '', en: '' },
  turnSoundOn: {
    az: 'Səsi aç',
    ru: 'Включить звук',
    en: 'Turn sound on',
  },
  // The booking keypad: a customer who booked elsewhere arriving by the code on
  // their confirmation. Native review pending (az, ru). "Back" is the one above.
  haveBooking: { az: 'Bronum var', ru: 'У меня есть запись', en: 'I have a booking' },
  typeCode: {
    az: 'Bron təsdiqindəki kodu daxil edin',
    ru: 'Введите код из подтверждения записи',
    en: 'Type the code on your booking confirmation',
  },
  arrive: { az: 'Təsdiq et', ru: 'Подтвердить', en: 'Arrive' },
  clear: { az: 'Təmizlə', ru: 'Очистить', en: 'Clear' },
  arriving: { az: 'Bronunuz yoxlanılır…', ru: 'Проверяем запись…', en: 'Finding your booking…' },
  bookedFor: { az: 'Bron vaxtınız: {time}', ru: 'Ваше время: {time}', en: 'Your booking is at {time}' },
  bookingNotFound: {
    az: 'Bu kodla bron tapılmadı. Kodu yoxlayın və ya qəbula müraciət edin.',
    ru: 'Мы не нашли запись с этим кодом. Проверьте его или обратитесь к администратору.',
    en: 'We could not find a booking with that code. Check it, or ask at reception.',
  },
  bookingNotToday: {
    az: 'Bronunuz {day} üçündür. Zəhmət olmasa, o gün gəlin.',
    ru: 'Ваша запись на {day}. Пожалуйста, приходите в этот день.',
    en: 'Your booking is for {day}. Please come back then.',
  },
  bookingArrived: {
    az: 'Bu bron artıq qeydiyyatdan keçib: talonunuz {number}.',
    ru: 'Эта запись уже отмечена: ваш талон {number}.',
    en: 'This booking has already arrived: your ticket is {number}.',
  },
  bookingArrivedBefore: {
    az: 'Bu bron artıq qeydiyyatdan keçib.',
    ru: 'Эта запись уже отмечена.',
    en: 'This booking has already arrived.',
  },
  // Too early for the check-in window (CHECKIN_TOO_EARLY): when it opens, branch time.
  checkInOpens: { az: '', ru: '', en: 'Check-in opens at {time}' },
  bookingElsewhere: {
    az: 'Bu bronu burada qeydiyyatdan keçirmək olmur. Zəhmət olmasa, qəbula müraciət edin.',
    ru: 'Эту запись нельзя отметить здесь. Пожалуйста, обратитесь к администратору.',
    en: 'This booking cannot be arrived here. Please ask at reception.',
  },
  // The machine has stopped looking codes up for a few minutes, after too many that
  // named nothing. Said without blame: the person reading it is usually not the one
  // who typed them.
  bookingTooMany: {
    az: 'Bu kiosk hazırda kod qəbul etmir. Zəhmət olmasa, qəbula müraciət edin.',
    ru: 'Этот киоск сейчас не принимает коды. Пожалуйста, обратитесь к администратору.',
    en: 'This machine is not taking codes just now. Please ask at reception.',
  },
  soundBlocked: {
    az: 'Səsi açmaq üçün ekrana toxunun',
    ru: 'Коснитесь экрана, чтобы включить звук',
    en: 'This screen cannot play sound until somebody presses it once',
  },
  // The feedback tablet at a desk (pages/Feedback). Asked in the visit's own
  // language, as the kiosk recorded it; idle in the machine's.
  feedbackIdle: {
    az: 'Xidmətdən sonra burada fikrinizi bildirə bilərsiniz',
    ru: 'После обслуживания вы можете оставить здесь отзыв',
    en: 'You can tell us how it went here, once you have been served',
  },
  feedbackAsk: {
    az: 'Xidmətdən razı qaldınızmı?',
    ru: 'Как вас обслужили?',
    en: 'How was your visit today?',
  },
  feedbackThanks: {
    az: 'Təşəkkür edirik!',
    ru: 'Спасибо!',
    en: 'Thank you!',
  },
  feedbackOffline: {
    az: 'Planşet hazırda əlaqədə deyil',
    ru: 'Планшет сейчас не на связи',
    en: 'This tablet is not connected just now',
  },
  feedbackNotATablet: {
    az: 'Bu ünvan rəy planşeti deyil',
    ru: 'Этот адрес — не планшет отзывов',
    en: 'This address is not a feedback tablet',
  },
  // A machine whose address was taken out of service (the server's 410 RETIRED
  // or BRANCH_RETIRED). Native review pending (az, ru).
  machineRetired: {
    az: 'Bu cihaz istifadədən çıxarılıb',
    ru: 'Это устройство выведено из работы',
    en: 'This machine has been taken out of service',
  },
  // A machine opened with no key, or an old one (SCREEN_KEY_REQUIRED / _REFUSED).
  machineNoKey: {
    az: 'Bu ekranın açarı yoxdur',
    ru: 'У этого экрана нет ключа',
    en: 'This screen needs its key',
  },
  // The hall board's one number, and the slip's validity: {n} and {time} are
  // filled by inQueueLine and validUntilLine. Native review pending (az, ru).
  inQueue: {
    az: 'Növbədə: {n} nəfər',
    ru: 'В очереди: {n}',
    en: 'In queue: {n}',
  },
  validUntil: {
    az: '{time}-dək etibarlıdır',
    ru: 'Действителен до {time}',
    en: 'Valid until {time}',
  },
  // A counter display's desk status on its Free screen. Native review pending (az, ru).
  deskFree: {
    az: 'Boşdur',
    ru: 'Свободно',
    en: 'Free',
  },
  deskBackSoon: {
    az: 'Tezliklə qayıdacağıq',
    ru: 'Скоро вернёмся',
    en: 'Back soon',
  },
  deskClosed: {
    az: 'Bağlıdır',
    ru: 'Закрыто',
    en: 'Closed',
  },
  // A counter display's status pill while a ticket is at the desk.
  deskCalled: {
    az: 'Çağırılır',
    ru: 'Вызван',
    en: 'Called',
  },
  deskServing: {
    az: 'Xidmətdə',
    ru: 'Обслуживается',
    en: 'In service',
  },
  // How boards and counter displays name a desk: {desk} its name, {number} its number.
  deskLabel: {
    az: '{desk}',
    ru: '{desk}',
    en: '{desk}',
  },
} satisfies Record<string, Phrase>

export type PhraseKey = keyof typeof phrases

/** t returns the phrase in the chosen language (i18n/catalog.ts, key kiosk.<key>), English when it has none. */
export function t(key: PhraseKey, lang: Language): string {
  const phrase = phrases[key]
  return wordText(`kiosk.${key}`, lang, phrase.en, lang === 'az' || lang === 'ru' ? phrase[lang] : undefined)
}

/** The failed screen's words: a busy machine waits, a busy branch is tried again. */
export function failedWords(reason: string): { lead?: PhraseKey; aside?: PhraseKey; retry: boolean } {
  if (reason === 'TOO_MANY_TICKETS') return { lead: 'busy', aside: 'orSeeReception', retry: false }
  if (reason === 'BRANCH_BUSY') return { lead: 'branchBusy', retry: true }
  return { lead: 'unavailable', retry: true }
}

/** The written language to say a thing in: English for any we do not write. */
const asLanguage = (lang: string): Written => (lang === 'az' || lang === 'ru' ? lang : 'en')

/** "In queue: 5", in the board's language (English for any we do not write). */
export function inQueueLine(n: number, lang: string): string {
  return t('inQueue', asLanguage(lang)).replace('{n}', String(n))
}

/** "Valid until 18:00", in the slip's language; the time is already on the branch's clock.
 *  The same words cmd/qms-agent prints on paper. */
export function validUntilLine(time: string, lang: string): string {
  return t('validUntil', asLanguage(lang)).replace('{time}', time)
}

/** A desk's state as a counter display words it; a closed desk never says "Free". */
export function deskStatusLine(status: 'open' | 'paused' | 'closed' | undefined, lang: string): string {
  const key: PhraseKey = status === 'paused' ? 'deskBackSoon' : status === 'closed' ? 'deskClosed' : 'deskFree'
  return t(key, asLanguage(lang))
}

/** What a counter display's status pill says: the ticket's state while one is at the desk, else the desk's. */
export type DeskPill = 'called' | 'serving' | 'open' | 'paused' | 'closed'

export function deskPillLine(pill: DeskPill | undefined, lang: string): string {
  if (pill === 'called') return t('deskCalled', asLanguage(lang))
  if (pill === 'serving') return t('deskServing', asLanguage(lang))
  return deskStatusLine(pill, lang)
}

/**
 * The server's estimate in the customer's words: "9 min", "15–20 min", "over 2
 * hours", "—" with no desk, '' with none; and the late flag after it.
 */
export function estimateInWords(e: Estimate | null | undefined, lang: Language): string {
  const min = t('minutes', lang)
  const words = waitText(e, {
    min: (m) => `${m} ${min}`,
    range: (low, high) => `${low}–${high} ${min}`,
    over: t('overTwoHours', lang),
  })
  return words && mayNotBeServed(e) ? `${words} · ${t('mayNotBeServed', lang)}` : words
}

/**
 * How many are in front of this customer, from the position the server gave.
 *
 * Unknown is not zero. The server answers 0 when it could not count, and a
 * waiting ticket is always at least first — so 0, like a missing answer, means
 * say nothing, not "you are next".
 */
export function aheadFromPosition(position: number | undefined): number | undefined {
  if (position === undefined || position < 1) return undefined
  return position - 1
}

/** The ahead line on the issued screen, in the customer's language. */
export function aheadSentence(ahead: number, lang: Language): string {
  return ahead === 0 ? t('nextInLine', lang) : `${t('peopleAhead', lang)}: ${ahead}`
}

/**
 * When the doors open again, written out rather than formatted.
 *
 * toLocaleString with an az-AZ locale answered "Wed" on the machine this was tested
 * on: the browser's ICU has no Azerbaijani weekday names and falls back to
 * abbreviated English, so an Azerbaijani sentence ended in an English word. This
 * file already exists because the product does not trust the runtime for its words,
 * and seven day names in three languages is a smaller thing to carry than a
 * dependency on what ICU a lobby machine happened to ship with.
 *
 * Each language gets the whole tail, not just the day, because the grammar differs:
 * Azerbaijani places the day bare, Russian needs the accusative after "в" and
 * another "в" before the time, English wants "at".
 */
const weekdays: Record<Written, [string, string, string, string, string, string, string]> = {
  // Sunday first, matching Date.getDay().
  az: [
    'bazar',
    'bazar ertəsi',
    'çərşənbə axşamı',
    'çərşənbə',
    'cümə axşamı',
    'cümə',
    'şənbə',
  ],
  ru: ['воскресенье', 'понедельник', 'вторник', 'среду', 'четверг', 'пятницу', 'субботу'],
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
}

/** Sunday-first, matching both Date.getDay() and the order above. */
const enShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** The zone if this runtime knows it, nothing if it does not. */
function usableZone(timeZone: string | undefined): string | undefined {
  if (!timeZone) return undefined
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(0)
    return timeZone
  } catch {
    return undefined
  }
}

/**
 * `timeZone` is the branch's, and it matters.
 *
 * The doors open at nine o'clock *there*. Reading the instant in the machine's own
 * zone was wrong in three ways that all reached a screen: the build server rendered
 * a 09:00 Baku opening as 05:00 and failed the gate, an operator previewing a
 * branch from another country would read the wrong hour, and a lobby machine whose
 * clock was set up carelessly would put a wrong opening time on the wall — the one
 * thing on that screen a customer might plan a journey around.
 *
 * Falls back to the machine's own zone when the branch has not been given one,
 * which is the behaviour every installation had before branches carried a timezone.
 */
export function dayAndTime(iso: string, lang: Language, timeZone?: string): string {
  if (!iso) return ''
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''

  // Checked once, before it is used twice. Intl throws a RangeError on a zone name
  // it does not know, and a branch whose timezone was mistyped would otherwise take
  // the closed screen down — leaving a kiosk showing nothing at the one moment its
  // only job is to say the building is shut.
  const zone = usableZone(timeZone)

  // Which day it is is a question about a place, not only about an instant: 23:30
  // in Baku is the previous day in London. Asked through en-US, whose weekday names
  // are in every ICU build there is — unlike Azerbaijani's, which is why the names
  // above are written out here in the first place.
  let index = at.getDay()
  if (zone) {
    const short = new Intl.DateTimeFormat('en-US', { timeZone: zone, weekday: 'short' }).format(at)
    const found = enShort.indexOf(short)
    if (found >= 0) index = found
  }

  const written = asLanguage(lang)
  const day = weekdays[written][index] ?? ''
  // Twenty-four hour, stated rather than inferred: Azerbaijan writes 09:00, and a
  // machine whose browser is set to an American locale must not put "9 AM" on a
  // wall in Baku.
  const time = at.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(zone ? { timeZone: zone } : {}),
  })

  switch (written) {
    case 'ru':
      return `в ${day} в ${time}`
    case 'az':
      return `${day} ${time}`
    default:
      return `${day} at ${time}`
  }
}

/** The calendar date (YYYY-MM-DD) of an instant on the branch's clock. */
function dateIn(at: Date, zone: string | undefined): string {
  return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', ...(zone ? { timeZone: zone } : {}) }).format(at)
}

/**
 * The kiosk's closed line: "We open tomorrow at 09:00", with today and tomorrow
 * named and any later day by its weekday, all on the branch's clock.
 */
export function opensWords(iso: string, lang: Language, timeZone: string | undefined, now: Date): string {
  if (!iso) return ''
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  const zone = usableZone(timeZone)
  const time = clockIn(iso, zone)
  const day = dateIn(at, zone)
  const today = dateIn(now, zone)
  // Noon avoids a clock change moving "tomorrow" by a day.
  const noon = new Date(`${today}T12:00:00Z`)
  noon.setUTCDate(noon.getUTCDate() + 1)
  const tomorrow = noon.toISOString().slice(0, 10)
  const near = day === today ? 'today' : day === tomorrow ? 'tomorrow' : null
  switch (asLanguage(lang)) {
    case 'ru':
      if (near) return `Откроемся ${near === 'today' ? 'сегодня' : 'завтра'} в ${time}`
      return `Откроемся ${dayAndTime(iso, lang, zone)}`
    case 'en':
      if (near) return `We open ${near} at ${time}`
      return `We open ${dayAndTime(iso, 'en', zone)}`
    default:
      // "Açılış: sabah, 09:00": no case ending on the time, which would follow its
      // spoken vowel (09:00-da, 08:00-də).
      if (near) return `Açılış: ${near === 'today' ? 'bu gün' : 'sabah'}, ${time}`
      return `Açılış: ${dayAndTime(iso, lang, zone).replace(/ (\d\d:\d\d)$/, ', $1')}`
  }
}

/** The wall-clock time of an instant in the branch's zone, 24-hour. */
export function clockIn(iso: string, timeZone?: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  const zone = usableZone(timeZone)
  return at.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(zone ? { timeZone: zone } : {}),
  })
}

/** The weekday a business day (YYYY-MM-DD) falls on, in the customer's language —
 *  the form a sentence about "coming back on" it takes. Read as a calendar date,
 *  not an instant, so no zone can move it. */
export function bookingDay(businessDate: string, lang: Language): string {
  const [y, m, d] = businessDate.split('-').map(Number)
  if (!y || !m || !d) return businessDate
  return weekdays[asLanguage(lang)][new Date(Date.UTC(y, m - 1, d)).getUTCDay()] ?? businessDate
}

/** serviceName picks the catalogue name: the chosen language, the organisation's default, English, any. */
export function serviceName(name: Record<string, string>, lang: Language, fallback: string, orgDefault?: Language): string {
  return resolveText(name, lang, orgDefault) || fallback
}
